import { keyBy } from 'lodash/fp';
import { FC, useCallback, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useMatch, useNavigate } from 'react-router-dom-v5-compat';

import useTableState from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { getPriorityWorkflowVersion } from 'dashboard/lib/workflows/util';
import { ProductFeatureName } from 'dashboard/models/organization';
import { TransactionTypeStatus } from 'dashboard/models/transaction-type';
import { Workflow, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowBlock } from 'dashboard/models/workflow-block';
import { WorkflowModule, WorkflowModuleStatus } from 'dashboard/models/workflow-module';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearWorkflowSchema } from 'dashboard/redux/actions/workflow-schema';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSort,
} from 'dashboard/redux/actions/workflows';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { useListWorkflowModulesQuery } from 'dashboard/redux/api/workflow-module';
import { RootState } from 'dashboard/redux/store';
import {
  archiveWorkflow,
  cloneWorkflow,
  createWorkflow,
  pauseWorkflow,
  runWorkflow,
} from 'dashboard/redux/thunk-actions/workflow';
import {
  fetchBaseObjectJsonSchemas,
  loadWorkflowSchemaParallel,
} from 'dashboard/redux/thunk-actions/workflow-schema';
import { loadWorkflowsPaginated } from 'dashboard/redux/thunk-actions/workflows';
import DashboardWorkflowsView from 'dashboard/views/DashboardWorkflowsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useProductFeature from 'lib/hooks/useProductFeature';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

export interface WorkflowModulesFilterParams {
  omni?: string;
  status?: WorkflowModuleStatus[];
  workflowIds?: string[];
}

const getPriorityWorkflowBlock = (workflowModule: WorkflowModule): string => {
  return (workflowModule.relationships['latest-draft-block'].data?.id ??
    workflowModule.relationships['latest-published-block'].data?.id) as string;
};

export const SupportedWorkflowTrigger = [
  ApiModel.TriggerApi,
  ApiModel.TriggerEvent,
  ApiModel.TriggerSchedule,
  ApiModel.TriggerRecurring,
];

const DashboardWorkflowsRoute: FC<PropsFromRedux> = ({
  archiveWorkflow,
  clearWorkflowSchema,
  cloneWorkflow,
  createWorkflow,
  currentEnvironment,
  currentPage,
  environments,
  fetchBaseObjectJsonSchemas,
  loadWorkflowSchemaParallel,
  loadWorkflowsPaginated,
  pageSize,
  paginationParams,
  pauseWorkflow,
  runWorkflow,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSort,
  showMessage,
  triggers,
  workflowDeployments,
  workflows,
  workflowSchema,
  workflowsFilterParams,
  workflowsSortParams,
  workflowVersions,
}) => {
  useOnMountAndUnmount(clearWorkflowSchema);
  const navigate = useNavigate();

  const matchRouteModules = useMatch({ path: '/dashboard/workflows/modules', end: true });
  const subRoute = useMemo<'workflows' | 'modules'>(
    () => (matchRouteModules != null ? 'modules' : 'workflows'),
    [matchRouteModules],
  );
  const switchRoute = useCallback(
    (tab: 'workflows' | 'modules') => {
      if (subRoute !== tab) {
        navigate(tab === 'workflows' ? '/dashboard/workflows' : '/dashboard/workflows/modules', {
          replace: true,
        });
      }
    },
    [navigate, subRoute],
  );
  const [hasFetched, setHasFetched] = useState(false);

  const {
    handleSortUpdate,
    handlePageLinkClick,
    handlePageSizeClick,
    isReady: isTableStateReady,
  } = useTable({
    currentPage,
    filters: workflowsFilterParams,
    pageSize,
    setCurrentPage,
    setFilters,
    setPageSize,
    setSortParams: setSort,
    sortParams: workflowsSortParams,
    key: 'Workflows',
  });

  const {
    tableState: workflowModulesTableState,
    paginationCallbacks: workflowModulesTableCallbacks,
  } = useTableState<WorkflowModulesFilterParams>({
    key: 'Modules',
    defaultFilterParams: {
      workflowIds: [],
      status: [WorkflowModuleStatus.Active],
    },
    filtersToApiFilters: ({ status, omni, workflowIds }: WorkflowModulesFilterParams) => ({
      status,
      omni,
      'workflow-ids': workflowIds,
    }),
  });

  const {
    error: modulesListQueryError,
    data: modulesListQueryResponse,
    isLoading: modulesListQueryIsLoading,
    isFetching: modulesListQueryIsFetching,
  } = useListWorkflowModulesQuery(workflowModulesTableState.apiOptions);

  useRTKErrorHandler({
    error: modulesListQueryError,
    onError: () => ({ message: 'Failed to load modules' }),
  });

  // Required to abort requests while props are changing quickly
  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadWorkflowsPaginated,
      currentEnvironment,
      currentPage,
      pageSize,
      workflowsSortParams,
      workflowsFilterParams,
    ],
  );

  const enablePlatformObjectJsonSchema = useFeatureFlag(
    'dashboard:workflow-use-platform-object-json-schema',
  );
  const { loading: isLoading, callback: fetchWorkflows } = useApiLoader({
    loader: async () => {
      if (!isTableStateReady) {
        return;
      }

      const { triggers: loadedTriggers } = await loadWorkflowsPaginated(
        workflowsFilterParams,
        currentPage,
        pageSize,
        workflowsSortParams,
        currentEnvironment.id,
        abortController.signal,
      );

      if (enablePlatformObjectJsonSchema) {
        const templateFilters = loadedTriggers
          .filter((trigger) => trigger.type === ApiModel.TriggerEvent)
          .flatMap((trigger) => trigger.attributes['template-filters']?.['transaction-type'] ?? []);
        await fetchBaseObjectJsonSchemas(
          [...new Set(templateFilters)].map((id) => ({
            type: ApiModel.Transaction,
            id,
          })),
        );
      }

      setHasFetched(true);
    },
    deps: [
      isTableStateReady,
      workflowsFilterParams,
      workflowsSortParams,
      currentEnvironment.id,
      currentPage,
      pageSize,
      abortController,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load workflows, please try again');
      }
    },
    onNetworkError: () => showMessage('Failed to load workflows, please try again'),
  });

  const { loading: isLoadingSchema } = useApiLoader({
    loader: async () => {
      return loadWorkflowSchemaParallel({
        loadCriteriaVariableSchemas: !enablePlatformObjectJsonSchema,
      });
    },
    deps: [currentEnvironment.id],
    onError: (error) => {
      if (isPermissionsError(error)) {
        showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        showMessage('Failed to load workflow schema, please try again');
      }
    },
    onNetworkError: () => showMessage('Failed to load workflow schema, please try again'),
  });

  const { data: { data: transactionTypes = [] } = {}, error: transactionTypesError } =
    useListTransactionTypesQuery({ filter: { status: [TransactionTypeStatus.Active] } });

  useRTKErrorHandler({
    error: transactionTypesError,
    onError: () => ({ message: 'Failed to load Transaction Types.', level: MessageType.Failure }),
  });

  const workflowModulesWithRelationships = useMemo(() => {
    return modulesListQueryResponse
      ? modulesListQueryResponse.workflowModules
          .filter(
            (workflowModule) => workflowModule.attributes.status !== WorkflowModuleStatus.Archived,
          )
          .map((workflowModule): [WorkflowModule, string] => [
            workflowModule,
            getPriorityWorkflowBlock(workflowModule),
          ])
          .map(([workflowModule, blockId]): [WorkflowModule, WorkflowBlock | null] => [
            workflowModule,
            modulesListQueryResponse?.workflowBlocks.find((wfb) => wfb.id === blockId) ?? null,
          ])
          .filter((row): row is [WorkflowModule, WorkflowBlock] => row[1] != null)
          .map(([workflowModule, workflowBlock]) => ({
            workflowModule,
            workflowBlock,
          }))
      : [];
  }, [modulesListQueryResponse]);

  const workflowsWithRelationships = useMemo(() => {
    const workflowDeploymentMap = keyBy('id', workflowDeployments);
    const triggersMap = keyBy('id', triggers);

    return workflows
      .filter((workflow) => workflow.attributes.status !== WorkflowStatus.Archived)
      .map((workflow): [Workflow, string] => [
        workflow,
        getPriorityWorkflowVersion(workflow, workflowDeploymentMap),
      ])
      .map(([workflow, versionId]): [Workflow, WorkflowVersion | null] => [
        workflow,
        workflowVersions.find((wfv) => wfv.id === versionId) ?? null,
      ])
      .filter((row): row is [Workflow, WorkflowVersion] => row[1] != null)
      .filter(([_workflow, workflowVersion]) =>
        SupportedWorkflowTrigger.some(
          (t) =>
            workflowVersion.relationships.trigger.data.type === t &&
            triggersMap[workflowVersion.relationships.trigger.data.id],
        ),
      )
      .map(([workflow, workflowVersion]) => ({
        workflow,
        workflowVersion,
        workflowDeployment:
          workflowDeploymentMap[workflow?.relationships?.['active-deployment']?.data?.id],
        trigger: triggersMap[workflowVersion.relationships.trigger.data.id],
      }));
  }, [workflows, workflowVersions, workflowDeployments, triggers]);

  const { callback: archiveWorkflowAndFetch } = useApiCallback(
    async (workflow: Workflow) => {
      await archiveWorkflow(workflow);
      await fetchWorkflows();
    },
    [archiveWorkflow, fetchWorkflows],
  );

  const { callback: pauseWorkflowAndFetch } = useApiCallback(
    async (workflow: Workflow) => {
      await pauseWorkflow(workflow);
      await fetchWorkflows();
    },
    [pauseWorkflow, fetchWorkflows],
  );

  const { callback: runWorkflowAndFetch } = useApiCallback(
    async (workflow: Workflow) => {
      await runWorkflow(workflow);
      await fetchWorkflows();
    },
    [runWorkflow, fetchWorkflows],
  );

  const hasWorkflowsFree = useProductFeature(ProductFeatureName.WorkflowsFree);
  const hasWorkflowsBasic = useProductFeature(ProductFeatureName.WorkflowsBasic);
  const hasWorkflowsEnhanced = useProductFeature(ProductFeatureName.WorkflowsEnhanced);

  return (
    <DashboardWorkflowsView
      currentTab={subRoute}
      environments={environments}
      handleWorkflowOmniFilterUpdate={(omni) => setFilters({ omni })}
      handleWorkflowPageLinkClick={handlePageLinkClick}
      handleWorkflowPageSizeClick={handlePageSizeClick}
      handleWorkflowSortUpdate={handleSortUpdate}
      handleWorkflowStatusFilterUpdate={(status) => setFilters({ status })}
      handleWorkflowTriggerFilterUpdate={(triggerTypes) => setFilters({ triggerTypes })}
      handleEventNameFilterUpdate={(eventNames) => setFilters({ eventNames })}
      workflowModulesTableState={workflowModulesTableState}
      workflowModulesTableCallbacks={workflowModulesTableCallbacks}
      hasWorkflowsBasic={hasWorkflowsBasic}
      hasWorkflowsEnhanced={hasWorkflowsEnhanced}
      hasWorkflowsFree={hasWorkflowsFree}
      workflowModulesWithRelationships={workflowModulesWithRelationships}
      isLoading={isLoading || !hasFetched}
      isLoadingModules={modulesListQueryIsLoading || modulesListQueryIsFetching}
      isLoadingSchema={isLoadingSchema}
      onArchiveWorkflow={archiveWorkflowAndFetch}
      onCreateWorkflow={(name, description, trigger) =>
        createWorkflow(currentEnvironment.id, name, trigger, description)
          .then(({ redirectPath }) => navigate(redirectPath))
          .catch((_err) => {
            showMessage('Failed to create workflow. Please try again later');
          })
      }
      referencedWorkflowFilterOptions={modulesListQueryResponse?.workflowOptions}
      workflowModulePaginationParams={modulesListQueryResponse?.paginationParams}
      onPauseWorkflow={pauseWorkflowAndFetch}
      onRunWorkflow={runWorkflowAndFetch}
      onCloneWorkflow={(workflow, name) =>
        cloneWorkflow(workflow, name)
          .then(({ redirectPath }) => {
            navigate(redirectPath);
            showMessage('Successfully made a copy of your workflow.', MessageType.Success);
          })
          .catch((_err) => {
            showMessage('Failed to make a copy of the workflow. Please try again later');
          })
      }
      onSelectTab={switchRoute}
      pageSize={pageSize}
      paginationParams={paginationParams}
      transactionTypes={transactionTypes}
      workflowSchema={workflowSchema}
      workflowsFilterParams={workflowsFilterParams}
      workflowsSortParams={workflowsSortParams}
      workflowsWithRelationships={workflowsWithRelationships}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  currentPage: state.workflows.currentPage,
  environments: state.organization.environments,
  pageSize: state.workflows.pageSize,
  paginationParams: state.workflows.paginationParams,
  triggers: state.workflows.triggers,
  workflowDeployments: state.workflows.workflowDeployments,
  workflows: state.workflows.workflows,
  workflowSchema: state.workflowSchema,
  workflowsFilterParams: state.workflows.filters,
  workflowsSortParams: state.workflows.sortParams,
  workflowVersions: state.workflows.workflowVersions,
});

const mapDispatchToProps = {
  archiveWorkflow,
  clearWorkflowSchema,
  cloneWorkflow,
  createWorkflow,
  fetchBaseObjectJsonSchemas,
  loadWorkflowSchemaParallel,
  loadWorkflowsPaginated,
  pauseWorkflow,
  runWorkflow,
  setCurrentPage,
  setFilters,
  setPageSize,
  setSort,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWorkflowsRoute);
